import React, { useState, useMemo } from 'react';
import { MapPin, Search, Star, TrendingUp, AlertCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ExternalLink, X, CheckCircle, User, Clock, FileText } from 'lucide-react';

// 支持的城市列表
const CITIES = [
  { id: 'mas', name: '马鞍山市', province: '安徽省' },
  { id: 'hz', name: '杭州市', province: '浙江省' },
  { id: 'nj', name: '南京市', province: '江苏省' },
  { id: 'hf', name: '合肥市', province: '安徽省' },
  { id: 'sh', name: '上海市', province: '上海市' },
  { id: 'tz', name: '台州市', province: '浙江省' },
  { id: 'fz', name: '福州市', province: '福建省' },
  { id: 'jx', name: '嘉兴市', province: '浙江省' },
];

// 城市选择器组件
const CityPicker = ({ visible, currentCity, cities, onSelect, onClose }) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center">
      <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[70vh] overflow-hidden shadow-2xl animate-slide-up">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 z-10">
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-800 text-lg">选择城市</span>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(70vh - 60px)' }}>
          <div className="mb-3">
            <span className="text-xs text-slate-500 font-medium">当前定位</span>
          </div>
          <div
            onClick={() => onSelect(currentCity)}
            className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200 mb-4 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-blue-500" />
              <span className="font-medium text-blue-700">{currentCity.name}</span>
            </div>
            <CheckCircle size={16} className="text-blue-500" />
          </div>

          <div className="mb-3">
            <span className="text-xs text-slate-500 font-medium">全部城市</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {cities.map((city) => (
              <div
                key={city.id}
                onClick={() => onSelect(city)}
                className={`p-3 rounded-xl border cursor-pointer transition-all text-center ${
                  currentCity.id === city.id
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
              >
                <div className="font-medium text-sm">{city.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{city.province}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// 统一的待处理提醒条（合并入口）
const PendingReminderBar = ({ taskCount, orderCount, onClick }) => {
  const totalCount = taskCount + orderCount;
  if (totalCount === 0) return null;

  return (
    <div className="fixed left-0 right-0 bottom-16 z-30 flex justify-center pointer-events-none">
      <div
        className="w-full max-w-md bg-white border-t-2 border-orange-200 shadow-2xl cursor-pointer active:bg-orange-50 transition-colors pointer-events-auto"
        onClick={onClick}
      >
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center animate-pulse shadow-md">
              <AlertCircle size={18} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">您有 {totalCount} 项待处理</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                {taskCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FileText size={10} className="text-orange-500" />
                    {taskCount}个结算单
                  </span>
                )}
                {orderCount > 0 && (
                  <span className="flex items-center gap-1">
                    <User size={10} className="text-purple-500" />
                    {orderCount}个订单
                  </span>
                )}
              </div>
            </div>
          </div>
          <ChevronUp size={20} className="text-orange-500 rotate-90" />
        </div>
      </div>
    </div>
  );
};

// 待处理弹窗组件
const PendingReminderModal = ({
  visible,
  onClose,
  tasks,
  orders,
  taskIndex,
  orderIndex,
  onTaskSwitch,
  onOrderSwitch,
  onViewTask,
  onViewOrder
}) => {
  const [activeTab, setActiveTab] = useState('orders');

  if (!visible) return null;

  const hasTasks = tasks && tasks.length > 0;
  const hasOrders = orders && orders.length > 0;

  const currentTask = hasTasks ? tasks[taskIndex] : null;
  const currentOrder = hasOrders ? orders[orderIndex] : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center">
      <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl animate-slide-up">
        {/* 头部 */}
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 z-10">
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-800 text-lg">待处理事项</span>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tab切换 */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
                activeTab === 'orders'
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <User size={16} />
                待确认订单
                {hasOrders && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === 'orders' ? 'bg-white/20' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {orders.length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
                activeTab === 'tasks'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <FileText size={16} />
                待确认结算
                {hasTasks && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === 'tasks' ? 'bg-white/20' : 'bg-orange-100 text-orange-600'
                  }`}>
                    {tasks.length}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          {/* 待确认订单Tab */}
          {activeTab === 'orders' && (
            hasOrders ? (
              <div>
                {/* 单个订单展示 */}
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded">{currentOrder.orderNo}</span>
                    <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full font-medium">待确认</span>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-700">{currentOrder.baseName}</span>
                  </div>

                  <div className="flex items-center justify-between py-3 border-y border-purple-100">
                    <div className="text-center flex-1">
                      <div className="text-xs text-slate-500 mb-1">物料</div>
                      <div className="text-sm font-medium text-slate-700 truncate">{currentOrder.material}</div>
                    </div>
                    <div className="text-center flex-1">
                      <div className="text-xs text-slate-500 mb-1">预估重量</div>
                      <div className="text-lg font-bold text-slate-800">{currentOrder.estimatedWeight}吨</div>
                    </div>
                    <div className="text-center flex-1">
                      <div className="text-xs text-slate-500 mb-1">预估金额</div>
                      <div className="text-lg font-bold text-red-600">¥{currentOrder.estimatedAmount?.toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <User size={12} />
                      <span>{currentOrder.supplierInfo?.name || '-'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{currentOrder.createdAt}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => onViewOrder(currentOrder)}
                    className="w-full mt-4 py-3 rounded-xl bg-purple-500 text-white text-sm font-bold hover:bg-purple-600 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <ExternalLink size={18} />
                    去确认订单
                  </button>
                </div>

                {/* 切换指示器 */}
                {orders.length > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                      onClick={() => onOrderSwitch('prev')}
                      disabled={orderIndex === 0}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        orderIndex === 0 ? 'bg-slate-100 text-slate-300' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <div className="flex gap-2">
                      {orders.map((_, i) => (
                        <div
                          key={i}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i === orderIndex ? 'bg-purple-500' : 'bg-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => onOrderSwitch('next')}
                      disabled={orderIndex === orders.length - 1}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        orderIndex === orders.length - 1 ? 'bg-slate-100 text-slate-300' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                  <User size={32} className="text-slate-300" />
                </div>
                <p className="text-slate-500 text-sm">暂无待确认订单</p>
              </div>
            )
          )}

          {/* 待确认结算Tab */}
          {activeTab === 'tasks' && (
            hasTasks ? (
              <div>
                {/* 单个任务展示 */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-4 border border-orange-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded">{currentTask.deliveryNo}</span>
                    <span className="text-xs text-slate-500">{currentTask.baseName}</span>
                  </div>

                  <div className="flex items-center justify-between py-3 border-y border-orange-100">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-1">入库重量</div>
                      <div className="text-lg font-bold text-slate-800">{currentTask.weight}吨</div>
                    </div>
                    <div className="text-2xl text-orange-300 font-light">×</div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-1">结算单价</div>
                      <div className="text-lg font-bold text-slate-800">¥{currentTask.unitPrice}/吨</div>
                    </div>
                    <div className="text-2xl text-orange-300 font-light">=</div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-1">结算金额</div>
                      <div className="text-2xl font-bold text-red-600">¥{currentTask.amount.toLocaleString()}</div>
                    </div>
                  </div>

                  <button
                    onClick={() => onViewTask(currentTask)}
                    className="w-full mt-4 py-3 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <ExternalLink size={18} />
                    查看详情并确认
                  </button>
                </div>

                {/* 切换指示器 */}
                {tasks.length > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                      onClick={() => onTaskSwitch('prev')}
                      disabled={taskIndex === 0}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        taskIndex === 0 ? 'bg-slate-100 text-slate-300' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <div className="flex gap-2">
                      {tasks.map((_, i) => (
                        <div
                          key={i}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i === taskIndex ? 'bg-orange-500' : 'bg-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => onTaskSwitch('next')}
                      disabled={taskIndex === tasks.length - 1}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        taskIndex === tasks.length - 1 ? 'bg-slate-100 text-slate-300' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                  <FileText size={32} className="text-slate-300" />
                </div>
                <p className="text-slate-500 text-sm">暂无待确认结算单</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default function HomePage({ bases, onBaseClick, getBaseImageUrl, pendingTasks, pendingConfirmOrders, onViewTaskDetail, onViewPendingOrder, cityDistances }) {
  const [taskIndex, setTaskIndex] = useState(0);
  const [orderIndex, setOrderIndex] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);

  // 计算待处理数量
  const taskCount = pendingTasks?.length || 0;
  const orderCount = pendingConfirmOrders?.length || 0;

  const [searchQuery, setSearchQuery] = useState('');
  const [currentCity, setCurrentCity] = useState(CITIES[0]);
  const [showCityPicker, setShowCityPicker] = useState(false);

  const handleTaskSwitch = (direction) => {
    if (direction === 'prev' && taskIndex > 0) {
      setTaskIndex(taskIndex - 1);
    } else if (direction === 'next' && taskIndex < pendingTasks.length - 1) {
      setTaskIndex(taskIndex + 1);
    }
  };

  const handleOrderSwitch = (direction) => {
    if (direction === 'prev' && orderIndex > 0) {
      setOrderIndex(orderIndex - 1);
    } else if (direction === 'next' && orderIndex < pendingConfirmOrders.length - 1) {
      setOrderIndex(orderIndex + 1);
    }
  };

  // 根据当前城市重新计算基地距离并排序
  const sortedBases = useMemo(() => {
    if (!cityDistances || !currentCity) {
      return bases;
    }

    // 计算每个基地到当前城市的距离
    const basesWithDistance = bases.map(base => {
      const baseCityId = base.cityId || 'mas'; // 默认马鞍山
      const distanceFromCurrentCity = cityDistances[currentCity.id]?.[baseCityId] || 0;
      // 实际距离 = 城市间距离 + 基地到其所在城市中心的距离
      const totalDistance = distanceFromCurrentCity + (base.distNum || 0);

      return {
        ...base,
        calculatedDist: totalDistance,
        displayDist: totalDistance < 1 ? `${Math.round(totalDistance * 1000)}m` : `${totalDistance.toFixed(1)}km`
      };
    });

    // 按距离排序
    return basesWithDistance.sort((a, b) => a.calculatedDist - b.calculatedDist);
  }, [bases, currentCity, cityDistances]);

  // 搜索过滤逻辑
  const filteredBases = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedBases;
    }
    const query = searchQuery.toLowerCase().trim();
    return sortedBases.filter(base =>
      base.name.toLowerCase().includes(query) ||
      base.address.toLowerCase().includes(query) ||
      base.businessDesc.toLowerCase().includes(query)
    );
  }, [sortedBases, searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleViewTask = (task) => {
    setShowPendingModal(false);
    onViewTaskDetail(task);
  };

  const handleViewOrder = (order) => {
    setShowPendingModal(false);
    onViewPendingOrder(order);
  };

  return (
    <div className="flex flex-col min-h-full bg-slate-50">
      {/* 页面头部 */}
      <div className="bg-white px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div
            onClick={() => setShowCityPicker(true)}
            className="flex items-center gap-2 text-slate-800 shrink-0 cursor-pointer active:opacity-70 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
              <MapPin size={16} className="text-white" />
            </div>
            <span className="font-semibold text-slate-800 text-sm">{currentCity.name}</span>
            <ChevronDown size={14} className="text-slate-400" />
          </div>
          <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-2">
            <Search size={16} className="text-slate-400 shrink-0" />
            <input
              type="text"
              placeholder="搜索基地名称或地址"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400 min-w-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="p-1 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X size={14} className="text-slate-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4">
        <div className="flex items-center justify-between mb-3 px-1">
          <h3 className="font-bold text-slate-800 text-base">附近基地</h3>
          <span className="text-xs text-blue-600 font-medium">
            {searchQuery ? `搜索结果 ${filteredBases.length} 家` : `共 ${filteredBases.length} 家`}
          </span>
        </div>

        {filteredBases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Search size={32} className="text-slate-300" />
            </div>
            <p className="text-slate-500 text-sm mb-2">未找到匹配的基地</p>
            <p className="text-slate-400 text-xs">请尝试其他关键词</p>
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="mt-4 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                清除搜索
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredBases.map((base) => (
              <div
                key={base.id}
                onClick={() => onBaseClick(base)}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 flex flex-col cursor-pointer active:scale-[0.98] transition-transform hover:shadow-md hover:border-blue-200"
              >
                <div className="aspect-[4/3] bg-slate-200 relative overflow-hidden">
                  <img
                    src={getBaseImageUrl(base.id)}
                    alt={base.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden absolute inset-0">
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-300 via-slate-200 to-slate-400">
                      <span className="text-slate-500 text-xs font-medium">基地图片</span>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                    <Star size={10} className="text-amber-500 fill-amber-500" />
                    <span className="text-[10px] font-bold text-slate-700">4.8</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
                </div>

                <div className="p-3 flex flex-col flex-1 min-h-0">
                  <div className="flex justify-between items-start mb-1.5">
                    <h4 className="font-bold text-slate-800 text-sm line-clamp-1 flex-1">{base.name}</h4>
                    <div className="flex items-center gap-0.5 text-blue-600 text-[10px] shrink-0 pt-0.5 font-medium">
                      <MapPin size={10} />
                      <span>{base.displayDist || base.dist}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <TrendingUp size={12} className="text-red-500" />
                    <p className="text-xs font-bold text-red-600">{base.priceRange}</p>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{base.businessDesc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 统一的待处理提醒条 */}
      <PendingReminderBar
        taskCount={taskCount}
        orderCount={orderCount}
        onClick={() => setShowPendingModal(true)}
      />

      {/* 待处理弹窗 */}
      <PendingReminderModal
        visible={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        tasks={pendingTasks}
        orders={pendingConfirmOrders}
        taskIndex={taskIndex}
        orderIndex={orderIndex}
        onTaskSwitch={handleTaskSwitch}
        onOrderSwitch={handleOrderSwitch}
        onViewTask={handleViewTask}
        onViewOrder={handleViewOrder}
      />

      {/* 城市选择器弹窗 */}
      <CityPicker
        visible={showCityPicker}
        currentCity={currentCity}
        cities={CITIES}
        onSelect={(city) => {
          setCurrentCity(city);
          setShowCityPicker(false);
        }}
        onClose={() => setShowCityPicker(false)}
      />
    </div>
  );
}
